/**
 * Tree view of placed module instances
 */

import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Box, Text, Stack, UnstyledButton, Group, ScrollArea, TextInput } from "@mantine/core";
import { useInstances, useSelectedModuleInstanceId } from "../store";
import { useWebSocket } from "../WebSocket";
import type { InstanceData } from "../types";

interface TreeNode {
  instance: InstanceData;
  children: TreeNode[];
}

function buildTree(instances: InstanceData[]): TreeNode[] {
  // Find root instances (no parent)
  const roots = instances.filter((inst) => inst.parent_instance_id === null);

  // Build tree recursively
  function buildNode(instance: InstanceData): TreeNode {
    const children = instances.filter(
      (inst) => inst.parent_instance_id === instance.instance_id
    );
    return {
      instance,
      children: children.map(buildNode),
    };
  }

  return roots.map(buildNode);
}

interface TreeNodeViewProps {
  node: TreeNode;
  depth: number;
  selectedInstanceId: string | null;
  editingInstanceId: string | null;
  onSelect: (instanceId: string) => void;
  onStartEdit: (instanceId: string) => void;
  onFinishEdit: (instanceId: string, newPrefix: string) => void;
  onCancelEdit: () => void;
  isLast: boolean;
}

function TreeNodeView({
  node,
  depth,
  selectedInstanceId,
  editingInstanceId,
  onSelect,
  onStartEdit,
  onFinishEdit,
  onCancelEdit,
  isLast,
}: TreeNodeViewProps) {
  const isSelected = selectedInstanceId === node.instance.instance_id;
  const isEditing = editingInstanceId === node.instance.instance_id;
  const inputRef = useRef<HTMLInputElement>(null);
  const [editValue, setEditValue] = useState("");

  // Get current prefix from instance data
  const instancePrefix = node.instance.prefix;

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Initialize with current prefix (if non-empty) or module name
      // Empty string prefix means "no prefix" mode, so show module_name for editing
      const initialValue = instancePrefix ? instancePrefix : node.instance.module_name;
      setEditValue(initialValue);
      inputRef.current.focus();
      // Delay select() to ensure value is set after React state update
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      });
    }
  }, [isEditing, instancePrefix, node.instance.module_name]);

  // Build tree line prefix using box-drawing characters
  const getTreePrefix = () => {
    if (depth === 0) return "";
    return isLast ? "└─ " : "├─ ";
  };

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // First select the instance, then start editing
    if (!isSelected) {
      onSelect(node.instance.instance_id);
    }
    // Small delay to allow selection to complete before editing
    setTimeout(() => {
      onStartEdit(node.instance.instance_id);
    }, 50);
  }, [isSelected, node.instance.instance_id, onSelect, onStartEdit]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onFinishEdit(node.instance.instance_id, editValue);
    } else if (e.key === "Escape") {
      onCancelEdit();
    }
  }, [editValue, node.instance.instance_id, onFinishEdit, onCancelEdit]);

  const handleEditBlur = useCallback(() => {
    onFinishEdit(node.instance.instance_id, editValue);
  }, [editValue, node.instance.instance_id, onFinishEdit]);

  // Display name: use prefix if available and non-empty, otherwise module_name
  // Empty string prefix means "no prefix" mode - show module_name
  const displayName = instancePrefix ? instancePrefix : node.instance.module_name;

  return (
    <Box>
      <UnstyledButton
        onClick={() => onSelect(node.instance.instance_id)}
        onDoubleClick={handleDoubleClick}
        style={{
          display: "block",
          width: "fit-content",
          minWidth: "100%",
          padding: "2px 8px",
          paddingLeft: `${8 + depth * 16}px`,
          borderRadius: 4,
          backgroundColor: isSelected ? "var(--mantine-color-blue-light)" : "transparent",
          border: isSelected ? "1px solid var(--mantine-color-blue-4)" : "1px solid transparent",
        }}
      >
        <Group gap={4} wrap="nowrap">
          {depth > 0 && (
            <Text
              size="xs"
              c="dimmed"
              style={{ fontFamily: "monospace", whiteSpace: "pre", flexShrink: 0 }}
            >
              {getTreePrefix()}
            </Text>
          )}
          {isEditing ? (
            <TextInput
              ref={inputRef}
              size="xs"
              value={editValue}
              onChange={(e) => setEditValue(e.currentTarget.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={handleEditBlur}
              onClick={(e) => e.stopPropagation()}
              styles={{
                input: {
                  minHeight: "unset",
                  height: "20px",
                  padding: "0 4px",
                  fontSize: "var(--mantine-font-size-sm)",
                },
              }}
            />
          ) : (
            <>
              <Text size="sm" style={{ whiteSpace: "nowrap" }}>
                {displayName}
              </Text>
              {instancePrefix === "" && (
                <Text size="xs" c="blue" style={{ whiteSpace: "nowrap" }}>
                  (no prefix)
                </Text>
              )}
            </>
          )}
          {!isEditing && node.instance.child_link_name && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              @{node.instance.child_link_name}
            </Text>
          )}
        </Group>
      </UnstyledButton>

      {/* Render children */}
      {node.children.map((child, index) => (
        <TreeNodeView
          key={child.instance.instance_id}
          node={child}
          depth={depth + 1}
          selectedInstanceId={selectedInstanceId}
          editingInstanceId={editingInstanceId}
          onSelect={onSelect}
          onStartEdit={onStartEdit}
          onFinishEdit={onFinishEdit}
          onCancelEdit={onCancelEdit}
          isLast={index === node.children.length - 1}
        />
      ))}
    </Box>
  );
}

export function InstanceTree() {
  const instances = useInstances();
  const selectedInstanceId = useSelectedModuleInstanceId();
  const { sendSelectInstance, sendSetInstancePrefix } = useWebSocket();
  const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);

  const tree = useMemo(() => buildTree(instances), [instances]);

  // Build a map of instance_id -> prefix for quick lookup
  const instancePrefixMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const inst of instances) {
      map.set(inst.instance_id, inst.prefix);
    }
    return map;
  }, [instances]);

  const handleSelect = useCallback((instanceId: string) => {
    sendSelectInstance(instanceId);
  }, [sendSelectInstance]);

  const handleStartEdit = useCallback((instanceId: string) => {
    setEditingInstanceId(instanceId);
  }, []);

  const handleFinishEdit = useCallback((instanceId: string, newPrefix: string) => {
    setEditingInstanceId(null);
    // Only send update if prefix actually changed
    const currentPrefix = instancePrefixMap.get(instanceId);
    if (newPrefix !== (currentPrefix ?? "")) {
      sendSetInstancePrefix(instanceId, newPrefix);
    }
  }, [instancePrefixMap, sendSetInstancePrefix]);

  const handleCancelEdit = useCallback(() => {
    setEditingInstanceId(null);
  }, []);


  if (instances.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        Drag modules to the scene to add them
      </Text>
    );
  }

  return (
    <ScrollArea type="auto" offsetScrollbars>
      <Stack gap={0}>
        {tree.map((node, index) => (
          <TreeNodeView
            key={node.instance.instance_id}
            node={node}
            depth={0}
            selectedInstanceId={selectedInstanceId}
            editingInstanceId={editingInstanceId}
            onSelect={handleSelect}
            onStartEdit={handleStartEdit}
            onFinishEdit={handleFinishEdit}
            onCancelEdit={handleCancelEdit}
            isLast={index === tree.length - 1}
          />
        ))}
      </Stack>
    </ScrollArea>
  );
}
